import { JSX } from "react";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { appContext } from "../../../../AppContext";
import { SerendieSymbol } from "@serendie/symbols";
import { Guid, PagedResponse } from "@iconics/core";
import { DataPoint } from "./DataModels";
import Helper from "../../../../Common/Helper";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, OnSectionUpdateHandler, RightButtons, TickSymbol } from "@iconics/components";
import { TypeHelper } from "@iconics/core";

export default class DataPointsCard extends IbssComponent<IProps, IState>
{
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);
        const { localStorageProvider } = appContext();

        this.state = {
            userCanAddDataPoints: localStorageProvider.hasRight('DATAMODEL.DataPoints.Create'),
            userCanUpdateDataPoints: localStorageProvider.hasRight('DATAMODEL.DataPoints.Update'),
            gridData: { rows: [] },
            gridRefreshToken: Guid.new().toString()
        };
    }

    private getDataPointTypeLabel(type: number): string
    {
        const { labels } = appContext();
        switch (type)
        {
            case 1:
                return labels.DataPoint_DataPointType_Realtime;
            case 2:
                return labels.DataPoint_DataPointType_Historical;
            default:
                return '';
        }
    }

    private handleDataPointEdit(id: string): void
    {
        if (!this.state.userCanUpdateDataPoints)
        {
            return;
        }
        this.props.onEdit(id);
    }
    
    public componentDidUpdate(prevProps: Readonly<IProps>): void
    {
        if (prevProps.nodeId !== this.props.nodeId)
        {
            this.refreshGrid();
        }
    }

    private refreshGrid(): void
    {
        this.setState({ gridRefreshToken: Guid.new().toString() });
    }

    private async handleRowsRequest(request: IGridRowsRequest<IDataPoint>): Promise<IGridRowsResponse<IDataPoint>>
    {
        const dataPointsData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.byNodeid.dataPoints.get<PagedResponse<DataPoint[]>>({
            select: DataPoint,
            tenancy: appContext().tenantId,
            nodeId: this.props.nodeId,
            skipToken: request.skipToken,
            top: request.take,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const dataPoints: IDataPoint[] = dataPointsData.value.map(x => ({
            id: x.DataPoint_Id,
            name: x.Name,
            displayName: this.languageValuesHelper.fromString(x.DisplayName),
            dataPointType: this.getDataPointTypeLabel(x.DataPointType),
            enabled: TypeHelper.toBoolean(x.IsEnabled) ?? false,
        }));

        return { rows: dataPoints, nextSkipToken: dataPointsData.skipToken };
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <Card
                title={labels.Platform_Node_DataPointListTitle}
                subTitle={labels.Platform_Node_DataPointListSubTitle}
                onSectionUpdate={i => this.props.onSectionUpdate(i)}
            >
                <GridContainer>
                    <GridItem>
                        <ButtonBar>
                            <RightButtons>
                                <Button
                                    size='small'
                                    styleType='ghost'
                                    leftIcon={<SerendieSymbol name='plus' />}
                                    onClick={() => this.props.onAdd()}
                                    disabled={!this.state.userCanAddDataPoints}
                                >
                                    {labels.Platform_CommonAction_Add}
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </GridItem>
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<DataPoint>("Name"),
                                    title: labels.DataPoint_Name,
                                    columnWidth: 30,
                                    sortable: true,
                                    getCellValue: i => i.name,
                                },
                                {
                                    columnId: Helper.nameOf<DataPoint>("DisplayName"),
                                    title: labels.DataPoint_DisplayName,
                                    columnWidth: 30,
                                    sortable: true,
                                    getCellValue: i => this.languageValuesHelper.getValue(i.displayName),
                                },
                                {
                                    columnId: Helper.nameOf<DataPoint>("DataPointType"),
                                    title: labels.DataPoint_DataPointType,
                                    columnWidth: 20,
                                    sortable: true,
                                    getCellValue: i => i.dataPointType,
                                },
                                {
                                    columnId: Helper.nameOf<DataPoint>("IsEnabled"),
                                    title: labels.DataPoint_Enabled,
                                    columnWidth: 20,
                                    sortable: true,
                                    getCellValue: i => i.enabled,
                                    template: i => <TickSymbol value={i.value as boolean} />,
                                },
                            ]}
                            data={this.state.gridData}
                            onChange={i => this.setState({ gridData: i })}
                            rowId={i => i.id}
                            onRowsRequest={i => this.handleRowsRequest(i)}
                            onCellClicked={i => this.handleDataPointEdit(i.id)}
                            refreshToken={this.state.gridRefreshToken}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }
}

export interface IProps
{
    nodeId: number;
    onAdd: () => void;
    onEdit: (id: string) => void;
    onSectionUpdate: OnSectionUpdateHandler;
}

export interface IState
{
    userCanAddDataPoints: boolean;
    userCanUpdateDataPoints: boolean;
    gridData: IGridData<IDataPoint>;
    gridRefreshToken: string;
}

export interface IDataPoint
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    dataPointType: string;
    enabled: boolean;
}
